import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ContadorService } from '../../../../../services/contador/contador.service';
import { PlanosService } from '../../../../../services/planos/planos.service';
import {
  PercentualComissaoAprovarDTO,
  SolicitacaoContadorConsultaDTO,
  StatusSolicitacaoContador,
} from '../../../../../models/contador/contador.model';
import { Plano } from '../../../../../models/planos/plano.model';

/** Severities aceitas pelo p-tag do PrimeNG. */
type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';

/**
 * Opcao do dropdown de plano na aprovacao. O dropdown lista TODOS os planos ATIVOS
 * e QUALQUER um e selecionavel (o backend aceita comum ou especial, sempre sem
 * cobranca). ESPECIAL => inscricao perpetua; comum => vence pela periodicidade.
 * `especial` apenas ordena (especiais primeiro) e define a pre-selecao recomendada.
 */
interface OpcaoPlano {
  label: string;
  value: number;
  descricao?: string;
  especial: boolean;
  /** Periodicidade do plano comum (MENSAL/TRIMESTRAL/ANUAL); exibida como meta. */
  tipoPlano?: string;
  /** Valor do plano (Guarani); exibido como meta nos comuns. */
  valor?: number;
  /** Usuarios que o plano ja inclui — piso sobre o qual o TOTAL informado na aprovacao soma. */
  usuariosInclusos?: number;
}

/**
 * Subcomponente da aba "Solicitações de Contador".
 *
 * Tabela paginada (lazy) com filtro por status (default PENDENTE) e acoes de
 * aprovar (dialog de confirmacao) e recusar (dialog com motivo opcional).
 * Acesso restrito via `masterAccessGuard` na rota pai. Fonte: `API_CONTRACT_contador.md`.
 */
@Component({
  selector: 'app-aprovacao-solicitacoes',
  templateUrl: './aprovacao-solicitacoes.component.html',
  styleUrls: ['./aprovacao-solicitacoes.component.scss'],
  providers: [MessageService],
})
export class AprovacaoSolicitacoesComponent implements OnInit, OnDestroy {
  // ===========================================================================
  // Listagem (tabela lazy)
  // ===========================================================================

  solicitacoes: SolicitacaoContadorConsultaDTO[] = [];
  first = 0;
  rows = 10;
  totalRecords = 0;
  loading = true;
  erroCarregar = false;

  /** Opcoes do dropdown "itens por pagina" do footer. */
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Ultimo valor de "itens por pagina" ja carregado; usado para descartar o
   *  (ngModelChange) que o dropdown dispara sem o usuario mudar a selecao. */
  private _lastRows = 10;

  /**
   * Sort da tabela (server-side). Só campos reais da entidade `SolicitacaoContador`
   * ordenam: Solicitado el (`solicitadoEm`) e Estado (`status`). Solicitante/Correo
   * (nome/e-mail do usuário) vêm de JOIN e não ordenam por esta rota.
   */
  private sortField: string | null = null;
  private sortOrder = 0; // 1 asc, -1 desc, 0 = default do backend

  private montarSort(): string {
    if (!this.sortField || this.sortOrder === 0) {
      return 'solicitadoEm,desc';
    }
    return `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /** Status atualmente filtrado (default PENDENTE). */
  statusAtual: StatusSolicitacaoContador = 'PENDENTE';

  /** Opcoes do filtro de status (label reconstruido no onLangChange). */
  statusOptions: { label: string; value: StatusSolicitacaoContador }[] = [];

  /** Termo de busca (nome OU e-mail do solicitante). Vazio => param nao enviado. */
  termoBusca = '';

  /** Emite a cada digitacao; debounce evita uma requisicao por tecla. */
  private busca$ = new Subject<string>();
  private buscaSub?: Subscription;

  // ===========================================================================
  // Dialog de aprovacao
  // ===========================================================================

  aprovarDialogVisivel = false;
  solicitacaoSelecionada: SolicitacaoContadorConsultaDTO | null = null;
  processando = false;

  /** Motivo OPCIONAL da aprovacao (max 500). Enviado no PATCH so quando preenchido. */
  motivoAprovacao = '';

  // ---------------------------------------------------------------------------
  // Plano na aprovacao — lista TODOS os planos ATIVOS (GET /planos/listar +
  // /planos/especiais); qualquer um e selecionavel (obrigatorio). O 1o ESPECIAL
  // vem pre-selecionado como default recomendado.
  // ---------------------------------------------------------------------------

  /** Opcoes do dropdown de plano (todos os ativos; especiais primeiro). */
  planosOptions: OpcaoPlano[] = [];
  /** Plano selecionado (id) — obrigatorio; pre-selecionado com o 1o ESPECIAL. */
  planoSelecionado: number | null = null;
  carregandoPlanos = false;
  erroPlanos = false;

  /** True quando ha ao menos um plano ESPECIAL na lista. */
  get temPlanoEspecial(): boolean {
    return this.planosOptions.some((o) => o.especial);
  }

  /**
   * True quando a carga terminou sem erro e NAO ha NENHUM plano cadastrado.
   * Unico estado que bloqueia totalmente a aprovacao (sem plano nao ha inscricao).
   */
  get semPlanos(): boolean {
    return !this.carregandoPlanos && !this.erroPlanos && this.planosOptions.length === 0;
  }

  /**
   * True quando ha planos, mas NENHUM e ESPECIAL. Aviso INFORMATIVO (nao
   * bloqueante): o master pode aprovar com um plano comum selecionado.
   */
  get apenasPlanosComuns(): boolean {
    return (
      !this.carregandoPlanos &&
      !this.erroPlanos &&
      this.planosOptions.length > 0 &&
      !this.temPlanoEspecial
    );
  }

  /** Lista fixa de percentuais que o master pode liberar ao contador na aprovacao. */
  readonly percentuaisOpcoes: { label: string; value: number }[] = [5, 10, 15, 20, 25, 30].map(
    (p) => ({ label: `${p}%`, value: p }),
  );

  /** Percentuais selecionados no dialog de aprovacao (liberados ao contador). */
  percentuaisSelecionados: number[] = [];

  /** Comissao (%) informada para cada percentual selecionado (chave = percentual). */
  comissoesPorPercentual: { [percentual: number]: number | null } = {};

  // ---------------------------------------------------------------------------
  // Usuarios de acesso do contador
  // ---------------------------------------------------------------------------

  /** Minimo aceito pelo backend (0 deixaria a empresa sem nem o acesso do dono). */
  readonly usuariosMinimo = 1;
  /** Teto do backend (`ContadorService.MAXIMO_USUARIOS_POR_CONTADOR`). */
  readonly usuariosMaximo = 100;

  /**
   * TOTAL de usuarios de acesso do contador (NAO um incremento).
   *
   * OBRIGATORIO na UI, embora o backend aceite nulo — mesmo tratamento dado aos percentuais.
   * Em branco, o contador herdaria em silencio o `usuariosInclusos` do plano (hoje 1 em todos)
   * e nao consegue comprar mais depois, porque plano ESPECIAL nao vende usuario avulso.
   */
  usuariosPermitidos: number | null = null;

  // ===========================================================================
  // Dialog de recusa
  // ===========================================================================

  recusarDialogVisivel = false;
  motivoRecusa = '';

  constructor(
    private contadorService: ContadorService,
    private planosService: PlanosService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildStatusOptions();
    this.translate.onLangChange.subscribe(() => this.buildStatusOptions());
    this.setupBuscaDebounce();
    this.carregarSolicitacoes(0, this.rows);
  }

  ngOnDestroy(): void {
    this.buscaSub?.unsubscribe();
    this.busca$.complete();
  }

  private buildStatusOptions(): void {
    const valores: StatusSolicitacaoContador[] = ['PENDENTE', 'APROVADA', 'RECUSADA'];
    this.statusOptions = valores.map((v) => ({
      value: v,
      label: this.translate.instant(`contador.status.${v}`),
    }));
  }

  // ===========================================================================
  // Listagem
  // ===========================================================================

  carregarSolicitacoes(page: number = 0, size: number = this.rows): void {
    this.loading = true;
    this.erroCarregar = false;

    const termo = this.termoBusca.trim();
    this.contadorService
      .listarSolicitacoes(this.statusAtual, page, size, termo, this.montarSort())
      .subscribe({
      next: (res) => {
        this.solicitacoes = res.content || [];
        this.totalRecords = res.totalElements ?? 0;
        this.first = page * size;
        this.loading = false;
      },
      error: () => {
        this.solicitacoes = [];
        this.totalRecords = 0;
        this.erroCarregar = true;
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('contador.aprovacao.erro_carregar'),
        });
      },
    });
  }

  /** Carregamento lazy da tabela (paginacao + ordenacao server-side). */
  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows ?? this.rows;
    const page = Math.floor((event.first ?? 0) / size);
    this.rows = size;
    this._lastRows = size;
    // A p-table lazy dispara o onLazyLoad ao clicar num cabecalho ordenavel,
    // ja com sortField/sortOrder (sem um (onSort) separado, que recarregaria em dobro).
    this.sortField = typeof event.sortField === 'string' ? event.sortField : null;
    this.sortOrder = this.sortField ? (event.sortOrder ?? 0) : 0;
    this.carregarSolicitacoes(page, size);
  }

  /** Troca de pagina pelo paginador externo. Como a tabela e lazy,
   *  recarregamos a pagina solicitada do backend. */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this._lastRows = size;
    this.carregarSolicitacoes(page, size);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega.
   *  O retorno antecipado ignora o disparo do dropdown sem mudanca de valor. */
  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) {
      return;
    }
    this._lastRows = this.rows;
    this.first = 0;
    this.carregarSolicitacoes(0, this.rows);
  }

  /** Troca o status filtrado e recarrega a partir da primeira pagina. */
  onStatusChange(): void {
    this.first = 0;
    this.carregarSolicitacoes(0, this.rows);
  }

  /** Assina o fluxo de busca com debounce; roda uma unica requisicao por termo. */
  private setupBuscaDebounce(): void {
    this.buscaSub = this.busca$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.first = 0;
        this.carregarSolicitacoes(0, this.rows);
      });
  }

  /** Digitou no campo de busca: guarda o termo e dispara o fluxo com debounce. */
  onBuscaChange(valor: string): void {
    this.termoBusca = valor || '';
    this.busca$.next(this.termoBusca.trim());
  }

  // ===========================================================================
  // Acoes — aprovar
  // ===========================================================================

  abrirDialogAprovar(solicitacao: SolicitacaoContadorConsultaDTO): void {
    this.solicitacaoSelecionada = solicitacao;
    this.percentuaisSelecionados = [];
    this.comissoesPorPercentual = {};
    this.planoSelecionado = null;
    this.usuariosPermitidos = null;
    this.motivoAprovacao = '';
    this.aprovarDialogVisivel = true;
    this.carregarPlanos();
  }

  /**
   * Carrega TODOS os planos para o seletor: combina `GET /planos/listar` (comuns)
   * com `GET /planos/especiais`. Os ESPECIAIS ficam no topo e selecionaveis; os
   * comuns entram desabilitados (contexto informativo). Pre-seleciona o primeiro
   * ESPECIAL da lista ordenada.
   *
   * `GET /planos/listar` lanca erro quando nao ha planos comuns; tratamos com
   * `catchError` como lista vazia (nao bloqueia — os comuns sao so informativos).
   */
  private carregarPlanos(): void {
    this.carregandoPlanos = true;
    this.erroPlanos = false;

    forkJoin({
      comuns: this.planosService.listarPlanos().pipe(catchError(() => of([] as Plano[]))),
      especiais: this.planosService.listarEspeciais(),
    }).subscribe({
      next: ({ comuns, especiais }) => {
        const idsEspeciais = new Set((especiais || []).map((p) => p.id));
        // Especiais primeiro (ordem do backend, por nome) + demais planos depois,
        // sem duplicar os que ja sao especiais.
        const opcoesEspeciais = (especiais || []).map((p) => this.toOpcaoPlano(p, true));
        const opcoesComuns = (comuns || [])
          .filter((p) => !idsEspeciais.has(p.id))
          .map((p) => this.toOpcaoPlano(p, false));
        this.planosOptions = [...opcoesEspeciais, ...opcoesComuns];
        // Pre-selecao: primeiro ESPECIAL da lista ordenada (se houver).
        this.planoSelecionado = opcoesEspeciais.length > 0 ? opcoesEspeciais[0].value : null;
        this.carregandoPlanos = false;
      },
      error: () => {
        this.planosOptions = [];
        this.planoSelecionado = null;
        this.erroPlanos = true;
        this.carregandoPlanos = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('contador.aprovacao.plano_especial_erro_carregar'),
        });
      },
    });
  }

  /** Converte um plano do backend numa opcao do dropdown (todos selecionaveis). */
  private toOpcaoPlano(p: Plano, especial: boolean): OpcaoPlano {
    return {
      label: p.nome,
      value: p.id,
      descricao: p.descricao,
      especial,
      tipoPlano: p.tipoPlano,
      valor: p.valor,
      usuariosInclusos: p.usuariosInclusos,
    };
  }

  /** Rotulo localizado da periodicidade do plano comum (MENSAL/TRIMESTRAL/ANUAL). */
  periodicidadeLabel(tipoPlano: string | undefined): string {
    if (!tipoPlano) {
      return '';
    }
    const chave = `contador.aprovacao.periodicidade.${tipoPlano.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? tipoPlano : traduzido;
  }

  /** Formata o valor do plano em Guarani (sem casas decimais). */
  formatarValorPlano(valor: number | undefined): string {
    const numero = valor ?? 0;
    return `Gs ${numero.toLocaleString('es-PY', { maximumFractionDigits: 0 })}`;
  }

  fecharDialogAprovar(): void {
    this.aprovarDialogVisivel = false;
    this.solicitacaoSelecionada = null;
  }

  confirmarAprovar(): void {
    if (this.processando || !this.solicitacaoSelecionada) {
      return;
    }

    // Sem NENHUM plano cadastrado, nao e possivel aprovar (nao ha inscricao).
    if (this.semPlanos) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.aprovacao.plano_vazio'),
      });
      return;
    }

    // Plano e OBRIGATORIO (o backend cria a inscricao do contador nele).
    if (this.planoSelecionado == null) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.aprovacao.plano_especial_obrigatorio'),
      });
      return;
    }

    // Precisa de ao menos um percentual selecionado.
    if (this.percentuaisSelecionados.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.aprovacao.comissao_obrigatoria'),
      });
      return;
    }

    // Cada percentual selecionado precisa de comissao preenchida e entre 0 e 100.
    const comissaoInvalida = this.percentuaisSelecionados.some((p) => {
      const comissao = this.comissoesPorPercentual[p];
      return comissao === null || comissao === undefined || comissao < 0 || comissao > 100;
    });
    if (comissaoInvalida) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.aprovacao.comissao_obrigatoria'),
      });
      return;
    }

    // OBRIGATORIO na UI: ver a justificativa no JSDoc de `usuariosPermitidos`.
    if (this.usuariosPermitidos == null) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.aprovacao.usuarios_obrigatorio'),
      });
      return;
    }

    // Mesma faixa do backend (422 `contador_usuarios_invalido`). O teste de inteiro e
    // defensivo: o campo ja formata sem decimais, mas o backend espera um Int.
    if (
      !Number.isInteger(this.usuariosPermitidos) ||
      this.usuariosPermitidos < this.usuariosMinimo ||
      this.usuariosPermitidos > this.usuariosMaximo
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.aprovacao.usuarios_invalido', {
          min: this.usuariosMinimo,
          max: this.usuariosMaximo,
        }),
      });
      return;
    }

    const percentuais: PercentualComissaoAprovarDTO[] = this.percentuaisSelecionados.map((p) => ({
      percentual: p,
      comissao: this.comissoesPorPercentual[p] as number,
    }));

    this.processando = true;
    const id = this.solicitacaoSelecionada.id;

    this.contadorService
      .aprovar(id, this.planoSelecionado, percentuais, this.motivoAprovacao, this.usuariosPermitidos)
      .subscribe({
        next: () => {
          this.processando = false;
          this.aprovarDialogVisivel = false;
          this.solicitacaoSelecionada = null;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('contador.aprovacao.sucesso_aprovar'),
          });
          this.carregarSolicitacoes(this.paginaAtual(), this.rows);
        },
        error: (err) => {
          this.processando = false;
          this.tratarErroAcao(err);
        },
      });
  }

  // ===========================================================================
  // Acoes — recusar
  // ===========================================================================

  abrirDialogRecusar(solicitacao: SolicitacaoContadorConsultaDTO): void {
    this.solicitacaoSelecionada = solicitacao;
    this.motivoRecusa = '';
    this.recusarDialogVisivel = true;
  }

  fecharDialogRecusar(): void {
    this.recusarDialogVisivel = false;
    this.solicitacaoSelecionada = null;
    this.motivoRecusa = '';
  }

  confirmarRecusar(): void {
    if (this.processando || !this.solicitacaoSelecionada) {
      return;
    }
    this.processando = true;
    const id = this.solicitacaoSelecionada.id;
    const motivo = (this.motivoRecusa || '').trim() || undefined;

    this.contadorService.recusar(id, motivo).subscribe({
      next: () => {
        this.processando = false;
        this.recusarDialogVisivel = false;
        this.solicitacaoSelecionada = null;
        this.motivoRecusa = '';
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador.aprovacao.sucesso_recusar'),
        });
        this.carregarSolicitacoes(this.paginaAtual(), this.rows);
      },
      error: (err) => {
        this.processando = false;
        this.tratarErroAcao(err);
      },
    });
  }

  private tratarErroAcao(err: any): void {
    const status = err?.status;
    if (status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.erros.sem_permissao'),
      });
      return;
    }
    // 422 (plano especial obrigatorio), 404 (plano/solicitacao inexistente) e 400
    // (plano nao e ESPECIAL) trazem mensagem amigavel do backend; priorizamos ela.
    const mensagemBackend = err?.error?.message;
    if (status === 404) {
      this.messageService.add({
        severity: 'error',
        summary: mensagemBackend || this.translate.instant('contador.erros.nao_encontrada'),
      });
      return;
    }
    this.messageService.add({
      severity: 'error',
      summary: mensagemBackend || this.translate.instant('contador.erros.generico'),
    });
  }

  // ===========================================================================
  // Helpers de exibicao
  // ===========================================================================

  /** Indica se a solicitacao admite acoes (apenas PENDENTE). */
  podeDecidir(solicitacao: SolicitacaoContadorConsultaDTO): boolean {
    return solicitacao.status === 'PENDENTE';
  }

  labelStatus(status: StatusSolicitacaoContador | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`contador.status.${status}`);
  }

  severityStatus(status: StatusSolicitacaoContador | string | undefined): TagSeverity {
    switch (status) {
      case 'APROVADA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'RECUSADA':
        return 'danger';
      default:
        return 'info';
    }
  }

  /** Formata data ISO 8601 em dd/MM/yyyy. */
  formatarData(dataISO: string | null | undefined): string {
    if (!dataISO) {
      return '';
    }
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  private paginaAtual(): number {
    return this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
  }
}
